import classNames from 'classnames';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import { RotateCwIcon, XIcon } from '@air/design-icons';
import {
  FileStatusButton,
  FileStatusPanelItem,
  FileStatusSubtitle,
  FileStatusTitle,
  type FileTrackingItem,
} from '@air/feature-file-status/ui';
import { VisuallyHidden } from '@air/primitive-visually-hidden';

import { SHARED_PANEL_ITEM_CLASS_NAMES } from '../../constants/ClassNames';
import { FileStatusThumbnail } from './FileStatusThumbnail';

export type FileStatusTrackingPanelListItemErroredProps = {
  item: FileTrackingItem;
  error: string;
  onCancel?: (item: FileTrackingItem) => void;
  onRetry?: (item: FileTrackingItem) => void;
};

export const FileStatusTrackingPanelListItemErrored = memo(
  ({
    item: { ext, title, thumbnailSource, onRowClick, previewLoader, avatar, status, disableCancel },
    item,
    error,
    onCancel,
    onRetry,
  }: FileStatusTrackingPanelListItemErroredProps) => (
    <FileStatusPanelItem
      avatar={avatar ?? <FileStatusThumbnail ext={ext} status={status} thumbnailSource={thumbnailSource} />}
      buttons={
        <>
          {onRetry && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Retry" onClick={() => onRetry(item)}>
              <RotateCwIcon size="sm" />
            </FileStatusButton>
          )}
          {onCancel && !disableCancel && (
            // biome-ignore lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
            <FileStatusButton appearance="ghost" label="Cancel" onClick={() => onCancel(item)}>
              <XIcon size="sm" />
            </FileStatusButton>
          )}
        </>
      }
      className={classNames(
        'hover:bg-gray-3',
        SHARED_PANEL_ITEM_CLASS_NAMES,
        !!onRowClick && 'cursor-pointer',
        !item.includeErrorInSummary && !onRetry && !(onCancel && !disableCancel) && 'opacity-60',
      )}
      onClick={onRowClick}
      previewLoader={previewLoader}
      subtitle={
        <FileStatusSubtitle className="truncate">
          <VisuallyHidden>with {title}</VisuallyHidden>
          <span aria-hidden="true">Error:</span> {error}
        </FileStatusSubtitle>
      }
      title={<FileStatusTitle className="text-12">{title}</FileStatusTitle>}
    />
  ),
  isEqual,
);

FileStatusTrackingPanelListItemErrored.displayName = 'FileStatusTrackingPanelListItemErrored';
